import React, { useState } from 'react';
import { FileText, Download, Share2, Plus, ChevronRight, Loader } from 'lucide-react';
import { reportTemplates, type ReportTemplate } from '../utils/reportTemplates';
import { generateReport, exportToPDF, shareReport } from '../utils/exportUtils';

const ReportsPage: React.FC = () => {
  const [selectedTemplate, setSelectedTemplate] = useState<ReportTemplate | null>(null);
  const [loading, setLoading] = useState(false);
  const [customSections, setCustomSections] = useState<string[]>([]);

  const handleGenerateReport = async () => {
    if (!selectedTemplate) return;
    
    setLoading(true);
    try {
      // In a real app, we would fetch real data here
      const sampleData = {
        demographics: {
          age: '25-45',
          income: '$75k-150k',
          education: "Bachelor's or higher"
        },
        metrics: {
          footTraffic: '12,000/day',
          competition: 'Low',
          growth: '+15% YoY'
        },
        competition: [
          { name: 'Business A', distance: '0.5mi', rating: 4.5 },
          { name: 'Business B', distance: '0.8mi', rating: 4.2 }
        ]
      };

      const doc = generateReport(selectedTemplate, sampleData);
      doc.save(`${selectedTemplate.id}-${new Date().getTime()}.pdf`);
    } catch (error) {
      console.error('Error generating report:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSection = () => {
    setCustomSections([...customSections, '']);
  };

  return (
    <div className="max-w-6xl mx-auto space-y-8">
      <div className="text-center space-y-4">
        <h1 className="text-4xl font-bold text-primary">Reports & Documentation</h1>
        <p className="text-xl text-muted-foreground">
          Generate and manage comprehensive location analysis reports
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {reportTemplates.map((template) => (
          <div
            key={template.id}
            className={`bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6 cursor-pointer transition-colors ${
              selectedTemplate?.id === template.id ? 'border-primary' : 'hover:border-primary/50'
            }`}
            onClick={() => setSelectedTemplate(template)}
          >
            <div className="flex items-start justify-between">
              <div className="flex items-center">
                <FileText className="h-6 w-6 text-primary" />
                <h2 className="text-xl font-semibold ml-2">{template.name}</h2>
              </div>
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
            <p className="mt-4 text-muted-foreground">{template.description}</p>
            <div className="mt-4 flex flex-wrap gap-2">
              {template.sections.map((section) => (
                <span
                  key={section.title}
                  className="px-2 py-1 bg-primary/10 text-primary text-sm rounded-lg"
                >
                  {section.title}
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>

      {selectedTemplate && (
        <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
          <h2 className="text-2xl font-semibold mb-6">Customize Report</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="font-medium mb-4">Template Sections</h3>
              <div className="space-y-2">
                {selectedTemplate.sections.map((section) => (
                  <div
                    key={section.title}
                    className="flex items-center justify-between p-4 bg-card/30 rounded-lg"
                  >
                    <span>{section.title}</span>
                    <span className="text-sm text-muted-foreground">{section.type}</span>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="font-medium mb-4">Custom Sections</h3>
              <div className="space-y-2">
                {customSections.map((_, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-4 bg-card/30 rounded-lg"
                  >
                    <input
                      type="text"
                      placeholder="Section title"
                      className="bg-transparent border-none focus:outline-none"
                    />
                    <select className="bg-transparent border-none focus:outline-none text-sm text-muted-foreground">
                      <option value="text">Text</option>
                      <option value="table">Table</option>
                      <option value="chart">Chart</option>
                      <option value="metrics">Metrics</option>
                    </select>
                  </div>
                ))}
                <button
                  onClick={handleAddSection}
                  className="w-full p-4 border border-dashed border-border rounded-lg hover:border-primary/50 transition-colors flex items-center justify-center"
                >
                  <Plus className="h-5 w-5 mr-2" />
                  Add Custom Section
                </button>
              </div>
            </div>

            <div className="flex space-x-4">
              <button
                onClick={handleGenerateReport}
                disabled={loading}
                className="flex-1 bg-primary text-primary-foreground py-2 rounded-lg hover:bg-primary/90 transition-colors flex items-center justify-center disabled:opacity-50"
              >
                {loading ? (
                  <>
                    <Loader className="animate-spin mr-2" size={20} />
                    Generating...
                  </>
                ) : (
                  <>
                    <Download className="mr-2" size={20} />
                    Generate Report
                  </>
                )}
              </button>
              
              <button
                onClick={() => shareReport(window.location.href)}
                className="flex-1 bg-secondary text-secondary-foreground py-2 rounded-lg hover:bg-secondary/90 transition-colors flex items-center justify-center"
              >
                <Share2 className="mr-2" size={20} />
                Share Report
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="bg-card/50 backdrop-blur-sm border border-border rounded-lg p-6">
        <h2 className="text-2xl font-semibold mb-6">Report Resources</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-2">
            <h3 className="font-medium">Templates</h3>
            <p className="text-sm text-muted-foreground">
              Access our library of professional report templates.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Data Export</h3>
            <p className="text-sm text-muted-foreground">
              Export your data in multiple formats including PDF and CSV.
            </p>
          </div>
          <div className="space-y-2">
            <h3 className="font-medium">Sharing</h3>
            <p className="text-sm text-muted-foreground">
              Share reports securely with team members and stakeholders.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;